import React, { useRef } from "react";
import ReactToPrint from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";

import { GlassOrderStatus } from "../../../enum/GlassOrder";
import { GlassOrder } from "../../../interfaces/GlassOrder";
import { getFullName } from "../../../helpers/formatters";
import usePrintStyles from "../../../hooks/usePrintStyles";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import LabPrintLetterHead from "../../Lab/LabPrint/LabprintComponents/LabPrintLetterhead";
import LabPrintLetterHeadCentered from "../../Lab/LabPrint/LabprintComponents/LabPrintLetterheadCentered";

// @ts-expect-error - image
import Logo from "../../../../assets/images/poweredByOkhati.svg";

import { PRESCRIPTION_COLS, PRESCRIPTION_ROWS } from "./GlassOrderCreateEdit";

interface GlassOrderPrintProps {
  glassOrder: GlassOrder;
}

export default function GlassOrderPrint({ glassOrder }: GlassOrderPrintProps): React.JSX.Element {
  const printRef = useRef<HTMLDivElement>(null);
  const rc = useCurrentResourceCentre();
  usePrintStyles({ pageStyle: "margin: 5mm 10mm" }).runPrintStyles();

  const centralizeHeader: boolean = rc?.settings?.printSettings?.centralizedHeader ?? false;
  const clinicNameFontScale = rc?.settings?.printSettings?.clinicNameFontScale;
  const panNo = rc?.settings?.billingSettings?.panNo;

  const letterHeadComponent = centralizeHeader ? (
    <LabPrintLetterHeadCentered
      resourceCentre={{
        ...rc,
        panNo,
        resourceCentreLogo: rc?.resourceCentreLogo ?? ""
      }}
      titleFontScale={clinicNameFontScale}
    />
  ) : (
    <LabPrintLetterHead
      resourceCentre={{
        ...rc,
        panNo,
        resourceCentreLogo: rc?.resourceCentreLogo ?? ""
      }}
      titleFontScale={clinicNameFontScale}
    />
  );

  const cellBorder = { border: "1px solid #ccc", padding: "6px 8px" };

  return (
    <div>
      <ReactToPrint
        trigger={() => (
          <IconButton
            size="small"
            title="Print Glass Order"
            sx={{
              color: "black",
              "&.MuiButtonBase-root:hover": {
                bgcolor: "transparent"
              }
            }}
          >
            <PrintIcon />
          </IconButton>
        )}
        content={() => printRef.current}
      />

      <iframe className="displayContents" title="printContainer">
        <div ref={printRef}>
          <Box component="table" width="100%">
            <thead>
              <tr>
                <td>
                  <Box sx={{ my: 2 }}>{letterHeadComponent}</Box>
                </td>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* Title */}
                    <Box
                      sx={{
                        textAlign: "center",
                        borderBottom: "1px solid #e0e0e0",
                        pb: 1
                      }}
                    >
                      <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                        GLASS ORDER
                      </Typography>
                    </Box>

                    {/* Patient & Order Info */}
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">
                          <strong>Patient: </strong> {getFullName(glassOrder.client)}
                        </Typography>
                        {glassOrder.client?.phone && (
                          <Typography variant="body2">
                            <strong>Phone: </strong> {glassOrder.client.phone}
                          </Typography>
                        )}
                        {glassOrder.client?.gender && (
                          <Typography variant="body2">
                            <strong>Gender: </strong> {glassOrder.client.gender}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ flex: 1, textAlign: "right" }}>
                        <Typography variant="body2">
                          <strong>Order Date: </strong>
                          {convertADtoBS(new Date(glassOrder.orderDate)).formatted4} BS
                        </Typography>
                        {glassOrder.status === GlassOrderStatus.DELIVERED &&
                          glassOrder.deliveryDate && (
                            <Typography variant="body2">
                              <strong>Delivery Date: </strong>
                              {convertADtoBS(new Date(glassOrder.deliveryDate)).formatted4} BS
                            </Typography>
                          )}
                        <Typography variant="body2">
                          <strong>Status: </strong>
                          <span style={{ textTransform: "capitalize" }}>
                            {glassOrder.status.replace(/_/g, " ")}
                          </span>
                        </Typography>
                      </Box>
                    </Box>

                    {/* Glass Prescription Table */}
                    <Box mt={1}>
                      <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                        PRESCRIPTION DETAILS
                      </Typography>
                      <TableContainer>
                        <Table size="small" sx={{ border: "1px solid #ccc" }}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                              <TableCell sx={{ ...cellBorder, fontWeight: 700, width: "120px" }}>
                                Eye
                              </TableCell>
                              {PRESCRIPTION_COLS.map((col) => (
                                <TableCell
                                  key={col.key}
                                  sx={{
                                    ...cellBorder,
                                    fontWeight: 700,
                                    textAlign: "center"
                                  }}
                                >
                                  {col.label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {PRESCRIPTION_ROWS.map((row) => {
                              const eyeData =
                                (glassOrder.prescription?.[row.key] as Record<
                                  string,
                                  string | number | null
                                >) || {};
                              return (
                                <TableRow key={row.key}>
                                  <TableCell sx={{ ...cellBorder, fontWeight: 700 }}>
                                    {row.label}
                                  </TableCell>
                                  {PRESCRIPTION_COLS.map((col) => (
                                    <TableCell
                                      key={col.key}
                                      sx={{ ...cellBorder, textAlign: "center" }}
                                    >
                                      {eyeData[col.key] ? String(eyeData[col.key]) : "-"}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>

                    {/* Specifications: Frame & Lens */}
                    {(glassOrder.frame || glassOrder.lens) && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 4,
                          p: 1.5,
                          border: "1px solid #eee",
                          backgroundColor: "#fafafa"
                        }}
                      >
                        {glassOrder.frame && (
                          <Typography variant="body2">
                            <strong>Frame: </strong> {glassOrder.frame}
                          </Typography>
                        )}
                        {glassOrder.lens && (
                          <Typography variant="body2">
                            <strong>Lens: </strong> {glassOrder.lens}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Payment Summary */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1
                      }}
                    >
                      <Box sx={{ width: "240px" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="body2">Total Amount:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Rs. {glassOrder.total ?? 0}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="body2">Advance Amount:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Rs. {glassOrder.advance ?? 0}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Balance Amount:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Rs. {glassOrder.balance ?? 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Remarks */}
                    {glassOrder.remarks && (
                      <Box>
                        <Typography variant="body2">
                          <strong>Remarks: </strong> {glassOrder.remarks}
                        </Typography>
                      </Box>
                    )}

                    {/* Signature */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        pt: 4,
                        mt: 2
                      }}
                    >
                      <Typography variant="body2">
                        Authorized Signature: ________________________________
                      </Typography>
                    </Box>
                  </Box>
                </td>
              </tr>
            </tbody>
          </Box>

          {/* Footer */}
          <div className="billFooterPos" style={{ width: "100%", marginTop: "20px" }}>
            <Box
              sx={{
                width: "100%",
                borderTop: "1px solid #ccc",
                pt: 1,
                display: "flex",
                justifyContent: "flex-end",
                px: 2
              }}
            >
              <Box sx={{ height: "30px" }}>
                <img src={Logo} alt="logo" height="100%" width="100%" />
              </Box>
            </Box>
          </div>
        </div>
      </iframe>
    </div>
  );
}
